import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  CreateReminderDtoCustomRecurrenceUnitEnum,
  CreateReminderDtoCustomRecurrenceWeekOfMonthEnum,
  CreateReminderDtoRecurrenceTypeEnum,
  GetProfileMedicationsDtoDosageIntervalEnum,
  GetProfileMedicationsDtoDurationTypeEnum,
  GetProfileMedicationsDtoFormEnum,
  GetProfileMedicationsDtoFrequencyTypeEnum,
  GetProfileMedicationsDtoStatusEnum,
  GetProfileMedicationsDtoStopReasonEnum,
  GetProfileMedicationsDtoStrengthUnitEnum,
} from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import {
  DURATIONS_WITH_VALUE,
  FREQUENCIES_WITH_VALUE,
  OTHER_OPTION,
} from './medicationFormOptions';
import {
  MedicationStockFormValues,
  medicationStockSchemaFields,
} from './medicationStockValues';


export type MedicationsFormModalValuesProps = {
  medicineProductId?: string;
  name?: string;
  preparationType?: string;

  // Dosing.
  strengthValue: number | '';
  strengthUnit: GetProfileMedicationsDtoStrengthUnitEnum | '';
  strengthUnitCustom: string;
  form: GetProfileMedicationsDtoFormEnum | '';
  formCustom: string;
  quantityValue: number | '';
  quantityCustom: string;
  dosagePending: boolean;
  frequencyType: GetProfileMedicationsDtoFrequencyTypeEnum | '';
  frequencyValue: number | '';
  frequencyCustom: string;
  durationType: GetProfileMedicationsDtoDurationTypeEnum | '';
  durationValue: number | '';
  durationCustom: string;
  dosageSchedule: string;

  // Clinical.
  indication: string;
  adverseReaction: boolean;
  adverseReactionNote: string;
  startedAt: Date | null;

  // Lifecycle.
  status: GetProfileMedicationsDtoStatusEnum;
  stopReason: GetProfileMedicationsDtoStopReasonEnum | '';
  stopReasonNote: string;
  endedAt: Date | null;

  /** @deprecated Derived from `quantityValue` when the form is submitted. */
  dosage: number;
  /** @deprecated Derived from `frequencyType` when the form is submitted. */
  dosageInterval: GetProfileMedicationsDtoDosageIntervalEnum;
  notes?: string;

  reminderEnabled: boolean;
  reminderDescription: string;
  reminderScheduledAt: Date | null;
  reminderRecurrenceType: CreateReminderDtoRecurrenceTypeEnum;
  reminderCustomEvery: number;
  reminderCustomUnit: CreateReminderDtoCustomRecurrenceUnitEnum;
  reminderCustomDayOfWeek: number | '';
  reminderCustomWeekOfMonth:
    | CreateReminderDtoCustomRecurrenceWeekOfMonthEnum
    | '';
  reminderEmailEnabled: boolean;
  reminderSmsEnabled: boolean;
} & MedicationStockFormValues;

/** An empty number input arrives as `''`; Yup would otherwise call it NaN. */
const optionalNumber = () =>
  Yup.number().transform((value, original) =>
    original === '' || original === null ? undefined : value,
  );

const requiredText = (messageKey: string) =>
  Yup.string().required(translateKey(messageKey));

/**
 * Dosing is required — that is the point of the rebuilt form — except when the
 * vet says the dose is still to be decided, which happens when a medication is
 * attached at a treatment before the patient has been weighed.
 */
export const MedicationSchema = Yup.object().shape({
  medicineProductId: Yup.string().optional(),
  name: Yup.string().when('medicineProductId', {
    is: (val: string | undefined) => !val,
    then: (schema) => schema.required(translateKey('medication.form.nameRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  preparationType: Yup.string().nullable().optional(),

  dosagePending: Yup.boolean().required(),

  strengthValue: optionalNumber().when('dosagePending', {
    is: false,
    then: (schema) =>
      schema
        .min(0, translateKey('medication.form.notNegative'))
        .required(translateKey('medication.form.strengthRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  strengthUnit: Yup.string().when('dosagePending', {
    is: false,
    then: (schema) =>
      schema.required(translateKey('medication.form.strengthUnitRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  strengthUnitCustom: Yup.string().when('strengthUnit', {
    is: OTHER_OPTION,
    then: () => requiredText('medication.form.customRequired'),
    otherwise: (schema) => schema.optional(),
  }),

  form: Yup.string().when('dosagePending', {
    is: false,
    then: (schema) =>
      schema.required(translateKey('medication.form.formRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  formCustom: Yup.string().when('form', {
    is: OTHER_OPTION,
    then: () => requiredText('medication.form.customRequired'),
    otherwise: (schema) => schema.optional(),
  }),

  // `moreThan` belongs inside the branch, not chained ahead of it: a legacy
  // record carries `dosage: 0`, so a rule applied unconditionally rejected the
  // very medication the vet ticked "dawka do ustalenia" to get past.
  quantityValue: optionalNumber().when('dosagePending', {
    is: false,
    then: (schema) =>
      schema
        .moreThan(0, translateKey('medication.form.quantityPositive'))
        .required(translateKey('medication.form.quantityRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  // Still on the model — the API accepts a descriptive amount — but the form
  // asks for a number and nothing else, so it has no field of its own.
  quantityCustom: Yup.string().optional(),

  frequencyType: Yup.string().when('dosagePending', {
    is: false,
    then: (schema) =>
      schema.required(translateKey('medication.form.frequencyRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  frequencyValue: optionalNumber().when('frequencyType', {
    is: (value: GetProfileMedicationsDtoFrequencyTypeEnum) =>
      FREQUENCIES_WITH_VALUE.includes(value),
    then: (schema) =>
      schema
        .moreThan(0, translateKey('medication.form.quantityPositive'))
        .required(translateKey('medication.form.frequencyValueRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  frequencyCustom: Yup.string().when('frequencyType', {
    is: OTHER_OPTION,
    then: () => requiredText('medication.form.customRequired'),
    otherwise: (schema) => schema.optional(),
  }),

  durationType: Yup.string().when('dosagePending', {
    is: false,
    then: (schema) =>
      schema.required(translateKey('medication.form.durationRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  durationValue: optionalNumber().when('durationType', {
    is: (value: GetProfileMedicationsDtoDurationTypeEnum) =>
      DURATIONS_WITH_VALUE.includes(value),
    then: (schema) =>
      schema
        .moreThan(0, translateKey('medication.form.quantityPositive'))
        .required(translateKey('medication.form.durationValueRequired')),
    otherwise: (schema) => schema.optional(),
  }),
  durationCustom: Yup.string().when('durationType', {
    is: OTHER_OPTION,
    then: () => requiredText('medication.form.customRequired'),
    otherwise: (schema) => schema.optional(),
  }),

  dosageSchedule: Yup.string().optional(),
  indication: Yup.string().optional(),
  adverseReaction: Yup.boolean().required(),
  adverseReactionNote: Yup.string().when('adverseReaction', {
    is: true,
    then: () => requiredText('medication.form.adverseReactionNoteRequired'),
    otherwise: (schema) => schema.optional(),
  }),
  startedAt: Yup.date().nullable().optional(),

  status: Yup.string().required(),
  stopReason: Yup.string().when('status', {
    is: 'STOPPED',
    then: () => requiredText('medication.form.stopReasonRequired'),
    otherwise: (schema) => schema.optional(),
  }),
  stopReasonNote: Yup.string().when('stopReason', {
    is: OTHER_OPTION,
    then: () => requiredText('medication.form.customRequired'),
    otherwise: (schema) => schema.optional(),
  }),
  endedAt: Yup.date().nullable().optional(),

  notes: Yup.string().optional(),

  reminderEnabled: Yup.boolean().required(),
  reminderDescription: Yup.string()
    .default('')
    .when('reminderEnabled', {
      is: true,
      then: (schema) =>
        schema.required('Opis przypomnienia jest wymagany').max(250),
      otherwise: (schema) => schema.optional(),
    }),
  reminderScheduledAt: Yup.date()
    .nullable()
    .default(null)
    .when('reminderEnabled', {
      is: true,
      then: (schema) => schema.required('Data jest wymagana').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
  reminderRecurrenceType: Yup.string().when('reminderEnabled', {
    is: true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  reminderCustomEvery: Yup.number().when('reminderEnabled', {
    is: true,
    then: (schema) => schema.min(1).required(),
    otherwise: (schema) => schema.optional(),
  }),
  reminderCustomUnit: Yup.string().when('reminderEnabled', {
    is: true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  reminderCustomDayOfWeek: Yup.mixed().optional(),
  reminderCustomWeekOfMonth: Yup.mixed().optional(),
  reminderEmailEnabled: Yup.boolean().required(),
  reminderSmsEnabled: Yup.boolean().required(),
  ...medicationStockSchemaFields,
});

// Stable resolver instance — a fresh `yupResolver(schema)` on every render
// would force react-hook-form to re-initialize its resolver state.
export const medicationFormResolver = yupResolver(MedicationSchema);

const FREQUENCY_TO_LEGACY_INTERVAL: Partial<
  Record<
    GetProfileMedicationsDtoFrequencyTypeEnum,
    GetProfileMedicationsDtoDosageIntervalEnum
  >
> = {
  TIMES_PER_DAY: 'Day',
  EVERY_N_HOURS: 'Day',
  EVERY_N_DAYS: 'Day',
  TIMES_PER_WEEK: 'Week',
  TIMES_PER_MONTH: 'Month',
};

/**
 * Fill the deprecated `dosage` / `dosageInterval` from the structured fields.
 * They stay written for one release so clients deployed before this — the
 * treatment editor among them — keep reading a value.
 */
export function withLegacyDosing(
  values: MedicationsFormModalValuesProps,
): MedicationsFormModalValuesProps {
  const quantity =
    typeof values.quantityValue === 'number' ? values.quantityValue : 0;
  const interval = values.frequencyType
    ? FREQUENCY_TO_LEGACY_INTERVAL[values.frequencyType]
    : undefined;

  return {
    ...values,
    dosage: quantity,
    dosageInterval: interval ?? values.dosageInterval ?? 'Day',
  };
}
